/**
 * Player-facing explanations must display ARABIC ONLY. The backend stores them
 * multilingually — some pure Arabic, some bilingual (`"عربي | English"`), some
 * pure Latin (`"Aimé Jacquet"`, `"Ampere"`). This helper distills the Arabic a
 * player should see, or `undefined` when there's nothing Arabic to show.
 *
 *  - No Arabic characters at all → `undefined` (pure-Latin is hidden).
 *  - Bilingual `" | "` separator → keep only the Arabic-bearing segment(s),
 *    rejoined with `" | "`.
 *  - A trailing parenthetical containing no Arabic (e.g. `"(D-Day)"`) is dropped;
 *    a parenthetical that contains Arabic is kept.
 *  - Trimmed; if nothing Arabic survives the filtering, `undefined`.
 */
const ARABIC_RE = /[؀-ۿ]/

export function arabicOnlyExplanation(
  explanation?: string | undefined,
): string | undefined {
  if (!explanation || !ARABIC_RE.test(explanation)) {
    return undefined
  }

  let result = explanation

  // Bilingual: keep only the segments that carry Arabic.
  if (result.includes(' | ')) {
    result = result
      .split(' | ')
      .map((segment) => segment.trim())
      .filter((segment) => ARABIC_RE.test(segment))
      .join(' | ')
  }

  // Drop a trailing pure-Latin parenthetical (e.g. "(D-Day)") while keeping
  // any parenthetical that itself contains Arabic.
  result = result.replace(/\s*\(([^()]*)\)\s*$/, (match, inner: string) =>
    ARABIC_RE.test(inner) ? match : '',
  )

  result = result.trim()
  return result && ARABIC_RE.test(result) ? result : undefined
}
